import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Button,
  Select,
  MenuItem,
  ListItemText,
  FormControl,
  InputLabel,
  OutlinedInput,
  Grid,
  Stack,
  Autocomplete,
  TextField
} from '@mui/material';
import { getClientsCustomReport } from 'src/services/clientService';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { BarLoader } from 'react-spinners';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 450
    }
  }
};

const styles = {
  cell: {
    border: '1px solid #E3E1D9',
    borderCollapse: 'collapse',
    padding: '6px'
  },
  cellTotal: {
    border: '1px solid #E3E1D9',
    borderCollapse: 'collapse',
    padding: '6px',
    fontWeight: 'bold'
  }
};
// Field labels mapping - actual names as they appear in the form
const fieldLabels: Record<string, string> = {
  // Basic Tab Fields
  clientId: 'Client ID',
  federalTaxId: 'Federal Tax ID',
  businessStructure: 'Business Structure',
  taxClassification: 'Tax Classification',
  legalBusinessName: 'Legal Business Name',
  doingBusinessAs: 'Doing Business As',
  industry: 'Industry',
  otherIndustry: 'Other Industry',
  sosNumber: 'SOS #',
  businessFormationDate: 'Business Formation Date',
  streetAddress1: 'Street Address 1',
  streetAddress2: 'Street Address 2',
  city: 'City',
  county: 'County',
  state: 'State',
  zipCode: 'Zip Code',
  mailingAddressSameAsLocation: 'Same as Location Address ?',
  mailingStreetAddress1: 'Street Address 1 (Mailing)',
  mailingStreetAddress2: 'Street Address 2 (Mailing)',
  mailingCity: 'City (Mailing)',
  mailingCounty: 'County (Mailing)',
  mailingState: 'State (Mailing)',
  mailingZipCode: 'Zip Code (Mailing)',
  registeredAgent: 'Registered Agent',
  contactPerson: 'Contact Person',
  contactNumber: 'Contact Number',
  contactEmail: 'Contact Email',
  contactFax: 'Contact Fax',
  accountantName: 'Accountant Name',
  property: 'Property',
  salesTax: 'Sales Tax',
  businessTax: 'Business Tax',
  franchiseTax: 'Franchise Tax',
  franchiseTaxNumber: 'Franchise Tax Number',
  payroll: 'Payroll',
  inactive: 'Inactive',
  inactiveDate: 'In-Active Date',
  monthlyFees: 'Monthly Fees',
  monthlyFeesStartDate: 'Monthly Fees Start Date',
  annualReport: 'Annual Report',
  bookKeeping: 'Bookkeeping',
  taxReturnPreparation: 'Tax Return Preparation',
  tppReturnPreparation: 'TPP Return Preparation',
  annualReportDueDate: 'Annual Report Due Date',
  taxReturnDueDate: 'Tax Return Due Date',
  tppReturnDueDate: 'TPP Return Due Date',
  reasonForInactive: 'Reason for Inactive',
  otherReason: 'Other -Specify',

  // Sales Tax Tab Fields
  salesTaxId: 'Sales Tax ID',
  routingNumber: 'Routing #',
  accountNumber: 'Account #',
  stateHighTax: 'State High Tax',
  stateLowTax: 'State Low Tax',
  cityCountyTax: 'City/County Taxes',
  countyTax: 'County Tax',
  cityTax: 'City Tax',
  liquorTax: 'Liquor Tax',
  beerTax: 'Beer Tax',
  cbdTax: 'CBD Tax',
  specialTax: 'Special Tax',
  fees911: '911 Fees',
  discountRate: 'Discount Rate',
  contactName: 'Contact Name',
  salesStartDate: 'Sales Start Date',
  salesStopDate: 'Sales Stop Date',
  importantNotes: 'Important Notes',

  // Business Tax Tab Fields
  businessTaxId: 'Business Tax ID',
  locationId: 'Location ID',
  businessType: 'Business Type',
  classification: 'Classification',
  taxRate: 'Tax Rate',
  gasolineTaxPerGallonFederal: 'Gasoline Tax per Gallon (Federal)',
  gasolineTaxPerGallonState: 'Gasoline Tax per Gallon (State)',
  dieselTaxPerGallonFederal: 'Diesel Tax per Gallon (Federal)',
  dieselTaxPerGallonState: 'Diesel Tax per Gallon (State)',
  avgGasolineTaxPerGallonFederal: 'Avg Gasoline Tax per Gallon (Federal)',
  avgGasolineTaxPerGallonState: 'Avg Gasoline Tax per Gallon (State)',
  tobaccoTaxPackOf20Federal: 'Tobacco Tax on Cigs -Pack of 20 (Federal)',
  tobaccoTaxPackOf20State: 'Tobacco Tax on Cigs -Pack of 20 (State)',
  tobaccoTaxOnOtpOfWholesaleCostFederal:
    'Tobacco Tax on OTP - of wholesale cost (Federal)',
  tobaccoTaxOnOtpOfWholesaleCostState:
    'Tobacco Tax on OTP - of wholesale cost (State)',
  tobaccoTaxPerLargeCigarFederal:
    'Tobacco Tax on Large Cigar - Per Cigar (Federal)',
  tobaccoTaxPerLargeCigarState:
    'Tobacco Tax on Large Cigar - Per Cigar (State)',
  tobaccoTaxOnPipePerPoundFederal: 'Tobacco Tax on Pipe - Per pound (Federal)',
  tobaccoTaxOnPipePerPoundState: 'Tobacco Tax on Pipe - Per pound (State)',
  tobaccoTaxOnRyoPerPoundFederal: 'Tobacco Tax on RYO - Per pound (Federal)',
  tobaccoTaxOnRyoPerPoundState: 'Tobacco Tax on RYO - Per pound (State)',
  beerTaxPerGallonFederal: 'Beer Tax per Gallon (Federal)',
  beerTaxPerGallonState: 'Beer Tax per Gallon (State)',

  // Payroll Tab Fields
  eftpsPin: 'EFTPS PIN',
  withHoldingNumber: 'Withholding #',
  laborAccNumber: 'Labor A/c #',
  payrollSchedule: 'Payroll Schedule',
  payrollDeliveryMethod: 'Payroll Delivery Method',
  payrollW2DeliveryMethod: 'W2 Delivery Method',
  payrollProcessingDay: 'Payroll Processing Day',
  payrollStartDate: 'Payroll Start Date',
  payrollStopDate: 'Payroll Stop Date',

  // Ownership Tab Fields
  ssnFein: 'SSN/FEIN',
  fullLegalName: 'Full Legal Name',
  title: 'Title',
  type: 'Type',
  ownershipPercent: 'Ownership Percentage',

  // Notes Tab Fields
  additionalNotes: 'Additional Notes'
};

const getHeaderLabel = (key: string) => {
  return fieldLabels[key] || key;
};

// Field groups per tab
const tabConfig = [
  {
    label: 'Basic',
    key: 'Basic',
    fields: [
      'clientId',
      'federalTaxId',
      'businessStructure',
      'taxClassification',
      'legalBusinessName',
      'doingBusinessAs',
      'industry',
      'otherIndustry',
      'sosNumber',
      'businessFormationDate',
      'streetAddress1',
      'streetAddress2',
      'city',
      'county',
      'state',
      'zipCode',
      'mailingAddressSameAsLocation',
      'mailingStreetAddress1',
      'mailingStreetAddress2',
      'mailingCity',
      'mailingCounty',
      'mailingState',
      'mailingZipCode',
      'registeredAgent',
      'contactPerson',
      'contactNumber',
      'contactEmail',
      'contactFax',
      'property',
      'salesTax',
      'businessTax',
      'franchiseTax',
      'franchiseTaxNumber',
      'payroll',
      'inactive',
      'inactiveDate',
      'monthlyFees',
      'annualReport',
      'bookKeeping',
      'taxReturnPreparation',
      'tppReturnPreparation',
      'monthlyFeesStartDate',
      'annualReportDueDate',
      'taxReturnDueDate',
      'tppReturnDueDate',
      'reasonForInactive',
      'otherReason',
      'accountantName'
    ]
  },
  {
    label: 'Sales Tax',
    key: 'SalesTax',
    fields: [
      'salesTaxId',
      'routingNumber',
      'accountNumber',
      'stateHighTax',
      'stateLowTax',
      'cityCountyTax',
      'countyTax',
      'cityTax',
      'liquorTax',
      'beerTax',
      'cbdTax',
      'specialTax',
      'fees911',
      'discountRate',
      'contactName',
      'contactNumber',
      'contactEmail',
      'salesStartDate',
      'salesStopDate',
      'importantNotes'
    ]
  },
  {
    label: 'Business Tax',
    key: 'BusinessTax',
    fields: [
      'businessTaxId',
      'locationId',
      'businessType',
      'classification',
      'taxRate',
      'gasolineTaxPerGallonFederal',
      'gasolineTaxPerGallonState',
      'dieselTaxPerGallonFederal',
      'dieselTaxPerGallonState',
      'avgGasolineTaxPerGallonFederal',
      'avgGasolineTaxPerGallonState',
      'tobaccoTaxPackOf20Federal',
      'tobaccoTaxPackOf20State',
      'tobaccoTaxOnOtpOfWholesaleCostFederal',
      'tobaccoTaxOnOtpOfWholesaleCostState',
      'tobaccoTaxPerLargeCigarFederal',
      'tobaccoTaxPerLargeCigarState',
      'tobaccoTaxOnPipePerPoundFederal',
      'tobaccoTaxOnPipePerPoundState',
      'tobaccoTaxOnRyoPerPoundFederal',
      'tobaccoTaxOnRyoPerPoundState',
      'beerTaxPerGallonFederal',
      'beerTaxPerGallonState'
    ]
  },
  {
    label: 'Payroll',
    key: 'Payroll',
    fields: [
      'eftpsPin',
      'withHoldingNumber',
      'laborAccNumber',
      'payrollSchedule',
      'payrollDeliveryMethod',
      'payrollW2DeliveryMethod',
      'payrollProcessingDay',
      'payrollStartDate',
      'payrollStopDate',
      'importantNotes',
      'contactName',
      'contactNumber',
      'contactEmail'
    ]
  },
  {
    label: 'Ownership',
    key: 'Ownership',
    fields: ['ssnFein', 'fullLegalName', 'title', 'type', 'ownershipPercent']
  },
  {
    label: 'Notes',
    key: 'Notes',
    fields: ['additionalNotes']
  }
];

export default function ClientsCustom() {
  const [selectedFields, setSelectedFields] = useState<{
    [tab: string]: string[];
  }>({});
  const [reportData, setReportData] = useState<Record<string, any>[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);

  const handleSelectChange = (tab: string, value: string[]) => {
    setSelectedFields((prev) => ({ ...prev, [tab]: value }));
  };

  const handleGenerateReport = async () => {
    if (Object.keys(selectedFields).length === 0) return;
    setLoading(true);
    try {
      const nestedData = await getClientsCustomReport({ selectedFields });

      // Transform nested data to flat structure for display
      const flattenedData = nestedData.map((client: any) => {
        const flatClient: Record<string, any> = {};

        // Flatten each category's data with tab prefix to avoid collisions
        Object.keys(client).forEach((category) => {
          if (client[category] && typeof client[category] === 'object') {
            Object.keys(client[category]).forEach((field) => {
              flatClient[`${category}_${field}`] =
                client[category][field] ?? null;
            });
          }
        });

        return flatClient;
      });

      setReportData(flattenedData);

      const allFields: string[] = [];
      tabConfig.forEach((tab) => {
        const fields = selectedFields[tab.label];
        if (fields) {
          fields.forEach((f) => {
            const prefixedField = `${tab.key}_${f}`;
            if (!allFields.includes(prefixedField))
              allFields.push(prefixedField);
          });
        }
      });
      setColumns(allFields);
    } catch (err) {
      console.error('Failed to fetch report:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async (
    reportData: Record<string, any>[],
    columns: string[],
    getHeaderLabel: (key: string) => string
  ) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Ownership Report');

    worksheet.columns = columns.map((col) => {
      // Extract field name from prefixed column for header
      const fieldName = col.includes('_')
        ? col.split('_').slice(1).join('_')
        : col;
      return {
        header: getHeaderLabel(fieldName),
        key: col,
        width: 20
      };
    });

    let currentCol = 1;
    const groupRow = worksheet.getRow(1);

    tabConfig.forEach((tab) => {
      const tabFields = columns.filter((c) => c.startsWith(`${tab.key}_`));
      if (!tabFields.length) return;

      const start = currentCol;
      const end = currentCol + tabFields.length - 1;

      worksheet.mergeCells(1, start, 1, end);
      const cell = groupRow.getCell(start);
      cell.value = tab.label;
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1E293B' }
      };

      for (let colIndex = start; colIndex <= end; colIndex++) {
        const borderCell = groupRow.getCell(colIndex);
        borderCell.border = {
          top: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          bottom: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          left: { style: 'thin', color: { argb: 'FFFFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFFFF' } }
        };
      }

      currentCol = end + 1;
    });

    const headerRow = worksheet.getRow(2);
    columns.forEach((col, idx) => {
      const cell = headerRow.getCell(idx + 1);
      // Extract field name from prefixed column for header
      const fieldName = col.includes('_')
        ? col.split('_').slice(1).join('_')
        : col;
      cell.value = getHeaderLabel(fieldName);
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center',
        wrapText: true
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF2563EB' }
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        right: { style: 'thin', color: { argb: 'FFCBD5E1' } }
      };
    });

    reportData.forEach((row, index) => {
      const rowData: Record<string, any> = {};
      columns.forEach((col) => {
        const fieldName = col.includes('_')
          ? col.split('_').slice(1).join('_')
          : col;
        let val = col === 'sno' ? index + 1 : row[col];
        if (val === true) val = 'Yes';
        else if (val === false) val = 'No';
        else if (val == null || val === '') val = '';
        if (typeof val === 'string') val = val.replace(/\r?\n/g, ' ');
        if (fieldName === 'monthlyFees') {
          const numericValue =
            typeof val === 'number'
              ? val
              : parseFloat(val.toString().replace(/[^0-9.-]/g, ''));
          if (!Number.isNaN(numericValue)) {
            val = numericValue;
          }
        }
        rowData[col] = val;
      });
      worksheet.addRow(rowData);
    });

    worksheet.columns.forEach((col) => {
      let maxLength = col.header ? col.header.toString().length : 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const val = cell.value ? cell.value.toString() : '';
        maxLength = Math.max(maxLength, val.length);
      });
      col.width = maxLength + 5;
    });

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber <= 2) return;
      row.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
      row.eachCell((cell) => {
        if (rowNumber % 2 === 0) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF1F5F9' }
          };
        }
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
          bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
          left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
          right: { style: 'thin', color: { argb: 'FFE2E8F0' } }
        };
      });
    });

    worksheet.views = [{ state: 'frozen', ySplit: 2 }];

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), 'Clients_Custom_Report.xlsx');
  };

  const handleExportReport = () => {
    if (!reportData || reportData.length === 0) {
      alert('No data available to export');
      return;
    }
    exportToExcel(reportData, columns, getHeaderLabel);
  };

  return (
    <Box mx={5} my={2}>
      {loading && (
        <div className="loader-wrapper" style={{ textAlign: 'center' }}>
          <BarLoader color={'#1976D2'} loading={loading} width={'94vw'} />
        </div>
      )}
      <Typography
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          color: '#1976D2',
          fontSize: '1.5em',
          my: 2
        }}
      >
        Clients Custom Report
      </Typography>

      <Grid container spacing={2} alignItems="center">
        {/* 🔹 All selects */}
        {tabConfig.map((tab) => (
          <Grid item key={tab.label} xs={12} sm={6} md={2}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={tab.fields}
              value={selectedFields[tab.label] || []}
              onChange={(_, newValue) =>
                handleSelectChange(tab.label, newValue)
              }
              getOptionLabel={(option) => getHeaderLabel(option)}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {getHeaderLabel(option)}
                </li>
              )}
              renderTags={(selected) => (
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                  title={selected.map((s) => getHeaderLabel(s)).join(', ')} // tooltip full list
                >
                  {selected.map((s) => getHeaderLabel(s)).join(', ')}
                </span>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={tab.label}
                  size="small"
                  disabled={loading}
                />
              )}
              fullWidth
              sx={{
                '& .MuiAutocomplete-tag': {
                  display: 'none' // hide MUI chip container completely
                },
                '& .MuiAutocomplete-inputRoot': {
                  flexWrap: 'nowrap' // prevent wrapping
                }
              }}
            />
          </Grid>
        ))}

        {/* 🔹 Buttons */}
        <Grid item xs={12} sm="auto">
          <Stack direction="row" spacing={2} justifyContent="flex-start">
            <Button
              variant="contained"
              color="primary"
              sx={{ height: '40px' }}
              onClick={handleGenerateReport}
              disabled={loading}
            >
              Generate
            </Button>
            <Button
              variant="contained"
              color="success"
              sx={{ height: '40px' }}
              onClick={handleExportReport}
              disabled={!reportData.length || loading}
            >
              Export
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* 🔹 Dynamic Table */}
      <Box mt={3}>
        <Paper sx={{ width: '95vw', overflow: 'hidden' }} elevation={3}>
          <TableContainer sx={{ width: '100%', overflow: 'auto' }}>
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table
                stickyHeader
                aria-label="report table"
                sx={{
                  tableLayout: 'auto',
                  minWidth: columns.length * 200 // 🔹 each column gets ~200px space
                }}
              >
                <TableHead>
                  <TableRow>
                    {tabConfig.map((tab) => {
                      const fields = columns.filter((c) =>
                        c.startsWith(`${tab.key}_`)
                      );
                      if (fields.length === 0) return null;
                      return (
                        <TableCell
                          key={tab.key}
                          align="center"
                          colSpan={fields.length}
                          sx={styles.cellTotal}
                        >
                          {tab.label}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  <TableRow>
                    {columns.map((col) => {
                      // Extract field name from prefixed column (e.g., "Basic_contactName" -> "contactName")
                      const fieldName = col.includes('_')
                        ? col.split('_').slice(1).join('_')
                        : col;
                      return (
                        <TableCell
                          key={col}
                          sx={{
                            fontWeight: '800',
                            color: '#1976D2',
                            border: '1px solid #E3E1D9',
                            padding: '8px',
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            maxWidth:
                              fieldName === 'additionalNotes' ? 300 : 200
                          }}
                        >
                          <Box
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {getHeaderLabel(fieldName)}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <TableRow hover tabIndex={-1} key={index}>
                        {columns.map((col) => (
                          <TableCell
                            key={col}
                            sx={{
                              ...styles.cell,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              maxWidth: col === 'additionalNotes' ? 400 : 200
                            }}
                          >
                            <Box
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'auto'
                              }}
                            >
                              {row[col] === true
                                ? 'Yes'
                                : row[col] === false
                                ? 'No'
                                : row[col] ?? ''}
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[50, 100, 150, 200]}
            count={reportData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
}
